import { useMemo, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { useNavigate } from 'react-router-dom'
import type { OrderRow } from '../lib/api'
import { db } from '../lib/db'
import { clock, dayMonth, money, qtyToText } from '../lib/money'
import { useMediaQuery } from '../lib/hooks'
import { PageHead } from './PageHead'
import { ScooterGlyph } from './ScooterIcon'
import { TableGlyph } from './TableIcon'
import { OrderDetailSheet } from './OrderDetailSheet'

/**
 * Danh sách hoá đơn của bộ Modern: bảng bên trái, chi tiết đơn bên phải.
 *
 * Trạng thái ở đây là ba trạng thái <b>có thật</b> của đơn hàng trong app này — đang bán,
 * đã thu tiền, đã huỷ. Bản thiết kế có thêm "chờ xử lý" và "đang làm", nhưng đó là quy
 * trình bếp, thứ sản phẩm này cố ý không làm: quán 10 bàn không có ai đứng điều phối
 * phiếu bếp, và một trạng thái không ai bấm là một trạng thái luôn sai.
 */

type Filter = 'all' | 'open' | 'paid' | 'void'

const FILTERS: { id: Filter; label: string; tone: string }[] = [
  { id: 'all', label: 'Tất cả', tone: 'brand' },
  { id: 'open', label: 'Đang bán', tone: 'amber' },
  { id: 'paid', label: 'Đã thu tiền', tone: 'green' },
  { id: 'void', label: 'Đã huỷ', tone: 'clay' },
]

/**
 * Số dòng một trang.
 *
 * Tám dòng vừa đúng một màn hình máy bảng, kể cả hàng số trang bên dưới. Nhiều hơn là phải
 * cuộn, mà cuộn một bảng đang dò thì mất chỗ mắt đang đứng — cuộn xong nhìn lại không biết
 * mình vừa đọc tới dòng nào.
 */
const PAGE_SIZE = 8

const stateOf = (r: OrderRow): Filter =>
  r.voidedAt || r.status === 'Void' ? 'void' : r.status === 'Open' ? 'open' : 'paid'

const STATE_LABEL: Record<Filter, string> = {
  all: '',
  open: 'Đang bán',
  paid: 'Đã thu tiền',
  void: 'Đã huỷ',
}

interface Props {
  rows: OrderRow[]
  day: string
  onDay: (day: string) => void
  canPickDay: boolean
  query: string
  onQuery: (q: string) => void
  loading: boolean
  offline: boolean
  onVoided: () => void | Promise<void>
}

const todayInput = () => {
  const d = new Date()
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

export function OrdersBoard({
  rows,
  day,
  onDay,
  canPickDay,
  query,
  onQuery,
  loading,
  offline,
  onVoided,
}: Props) {
  const nav = useNavigate()
  const hasPane = useMediaQuery('(min-width: 1180px)')

  const [filter, setFilter] = useState<Filter>('all')
  const [kind, setKind] = useState<'all' | 'table' | 'away'>('all')
  const [pickedId, setPickedId] = useState<string | null>(null)
  const [pageAt, setPageAt] = useState(1)

  // Ô "Mang về" cũng là một cái bàn, nên phân loại tại bàn / mang đi phải tra tên bàn nào
  // là ô mang về. Tra trên máy: danh mục đã kéo sẵn về rồi, mất mạng vẫn lọc được.
  const awayNames = useLiveQuery(
    async () =>
      new Set(
        (await db.diningTables.filter((t) => t.isTakeaway).toArray()).map((t) => t.name),
      ),
    [],
    new Set<string>(),
  )

  /*
   * Lọc theo loại bán trước, lọc theo trạng thái sau.
   *
   * Thứ tự này quyết định con số trên bốn ô đếm: bấm "Mang về" thì bốn ô phải đếm lại theo
   * riêng đơn mang về. Ngược lại thì ô ghi 8 mà bảng bên dưới chỉ có 5 dòng, và người đọc
   * không biết tin con số nào.
   */
  const byKind = useMemo(
    () =>
      rows.filter((r) => {
        if (kind === 'away') return !!r.tableName && awayNames.has(r.tableName)
        if (kind === 'table') return !r.tableName || !awayNames.has(r.tableName)
        return true
      }),
    [rows, kind, awayNames],
  )

  /*
   * Mỗi ô đếm mang theo tổng tiền của đúng mấy đơn nó đếm.
   *
   * Không ô nào được gọi là "doanh thu": ô "Đang bán" là tiền chưa vào két, ô "Đã huỷ" là
   * tiền đã rời khỏi sổ. Cái tên trạng thái ngay bên cạnh con số nói rõ đó là tiền gì, nên
   * cộng đúng số đơn của mình là đủ, không cần khéo thêm.
   */
  const tally = useMemo(() => {
    const c: Record<Filter, number> = { all: byKind.length, open: 0, paid: 0, void: 0 }
    const m: Record<Filter, number> = { all: 0, open: 0, paid: 0, void: 0 }
    for (const r of byKind) {
      const st = stateOf(r)
      c[st]++
      m[st] += r.total
      m.all += r.total
    }
    return { counts: c, money: m }
  }, [byKind])

  const shown = useMemo(
    () => byKind.filter((r) => filter === 'all' || stateOf(r) === filter),
    [byKind, filter],
  )

  /*
   * Đổi bộ lọc hay đổi ngày thì về trang một.
   *
   * Không thì đang đứng ở trang 4, bấm "Đã huỷ" còn đúng hai đơn, và bảng trắng trơn —
   * người dùng tưởng mất dữ liệu chứ không nghĩ mình đang ở trang không tồn tại.
   */
  const pageCount = Math.max(1, Math.ceil(shown.length / PAGE_SIZE))
  const page = Math.min(pageAt, pageCount)
  const rowsOnPage = shown.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  const picked = shown.find((r) => r.id === pickedId) ?? null

  function pick(r: OrderRow) {
    // Đơn đang bán thì mở thẳng màn hình gọi món — người bán muốn thêm món, không phải
    // đọc lại tờ hoá đơn chưa chốt.
    if (stateOf(r) === 'open') {
      nav(`/order/${r.id}`)
      return
    }
    setPickedId(r.id)
  }

  return (
    <div className="board orders-board">
      <PageHead
        to="/"
        icon="🧾"
        tone="is-blue"
        title="Đơn hàng"
        sub="Quản lý và theo dõi tất cả đơn hàng"
      />

      <div className="orders-split">
        <div className="orders-main">
          {/*
            Một hàng công cụ, đọc từ trái sang: xem ngày nào, xem loại bán nào, tìm gì.
            Trước đây ba thứ này là ba khối rời nên chúng rơi xuống thành một cột dọc ngay
            khi màn hẹp lại, và cái nút cần bấm nhiều nhất thì trôi mỗi lúc một chỗ.
          */}
          <div className="orders-bar">
            {canPickDay ? (
              <label className="day-pick">
                <span className="sr-only">Ngày</span>
                <input
                  type="date"
                  value={day}
                  max={todayInput()}
                  onChange={(e) => {
                    onDay(e.target.value)
                    setPageAt(1)
                  }}
                />
              </label>
            ) : (
              <span className="chip" aria-disabled="true">
                Hôm nay
              </span>
            )}

            {/* Ba lựa chọn loại trừ nhau thì dính liền thành một khối, không rời ra ba nút
                trôi nổi — nhìn một cái là biết chúng thuộc cùng một câu hỏi. */}
            <div className="pill-seg" role="tablist" aria-label="Loại bán">
              <button
                type="button"
                role="tab"
                aria-selected={kind === 'all'}
                aria-pressed={kind === 'all'}
                onClick={() => {
                  setKind('all')
                  setPageAt(1)
                }}
              >
                Tất cả loại
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={kind === 'table'}
                aria-pressed={kind === 'table'}
                onClick={() => {
                  setKind('table')
                  setPageAt(1)
                }}
              >
                <TableGlyph className="chip-glyph" /> Tại bàn
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={kind === 'away'}
                aria-pressed={kind === 'away'}
                onClick={() => {
                  setKind('away')
                  setPageAt(1)
                }}
              >
                <ScooterGlyph className="chip-glyph" /> Mang về
              </button>
            </div>

            <label className="search">
              <span className="sr-only">Tìm hoá đơn</span>
              <span className="search-glyph" aria-hidden="true">
                ⌕
              </span>
              <input
                value={query}
                onChange={(e) => {
                  onQuery(e.target.value)
                  setPageAt(1)
                }}
                placeholder="Tìm số hoá đơn, tên bàn, số tiền…"
                inputMode="search"
              />
            </label>
          </div>

          <div className="stat-chips">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                className={`stat-chip is-${f.tone}`}
                aria-pressed={filter === f.id}
                onClick={() => {
                  setFilter(f.id)
                  setPageAt(1)
                }}
              >
                <span className="stat-chip-key">
                  {f.label}
                  <small className="num">
                    {loading ? '—' : `${money(tally.money[f.id])}đ`}
                  </small>
                </span>
                <b className="stat-chip-val num">{tally.counts[f.id]}</b>
              </button>
            ))}
          </div>

          {offline && (
            <p className="hint">
              Chưa lấy được dữ liệu từ máy chủ — đang xem hoá đơn lưu trên máy này. Đơn do máy
              khác trong quán bán sẽ hiện khi có mạng lại.
            </p>
          )}

          {shown.length === 0 ? (
            <div className="empty">
              <div className="empty-icon" aria-hidden="true">
                🧾
              </div>
              <p>
                {query
                  ? `Không có hoá đơn nào khớp "${query}".`
                  : loading
                    ? 'Đang tải…'
                    : 'Ngày này chưa có đơn nào.'}
              </p>
            </div>
          ) : (
            <div className="order-table" role="table">
              <div className="order-head" role="row">
                <span role="columnheader">Mã đơn</span>
                <span role="columnheader">Thời gian</span>
                <span role="columnheader">Loại</span>
                <span role="columnheader">Tổng tiền</span>
                <span role="columnheader">Trạng thái</span>
              </div>

              {rowsOnPage.map((r) => {
                const st = stateOf(r)
                const away = !!r.tableName && awayNames.has(r.tableName)

                return (
                  <button
                    key={r.id}
                    type="button"
                    role="row"
                    className={`order-row is-${st}${pickedId === r.id && hasPane ? ' is-picked' : ''}`}
                    onClick={() => pick(r)}
                  >
                    <span className="order-code num" role="cell">
                      {r.code}
                    </span>
                    <span className="order-time" role="cell">
                      <b>{r.paidAt ? clock(r.paidAt) : '—'}</b>
                      <small>{r.paidAt ? dayMonth(r.paidAt) : ''}</small>
                    </span>
                    <span className="order-kind" role="cell">
                      <span className={`kind-pill${away ? ' is-away' : ''}`}>
                        {away ? (
                          <ScooterGlyph className="chip-glyph" />
                        ) : (
                          <TableGlyph className="chip-glyph" />
                        )}{' '}
                        {r.tableName ?? 'Bán nhanh'}
                      </span>
                    </span>
                    <span className={`order-total num${st === 'void' ? ' struck' : ''}`} role="cell">
                      {money(r.total)}đ
                    </span>
                    <span className="order-state" role="cell">
                      <span className={`state-pill is-${st}`}>{STATE_LABEL[st]}</span>
                      <small>{qtyToText(r.itemCount * 1000)} món</small>
                    </span>
                  </button>
                )
              })}

              {/*
                Dòng trống cho đủ một trang.
                Trang cuối còn hai đơn thì bảng vẫn cao bằng trang đầy, nên hàng số trang
                đứng nguyên một chỗ — bấm mãi thành quen tay, mắt khỏi đi tìm lại.
              */}
              {pageCount > 1 &&
                Array.from({ length: PAGE_SIZE - rowsOnPage.length }, (_, i) => (
                  <div key={`pad-${i}`} className="order-row is-pad" role="presentation" aria-hidden="true">
                    <span className="order-code">&nbsp;</span>
                    <span className="order-time">
                      <b>&nbsp;</b>
                      <small>&nbsp;</small>
                    </span>
                    <span className="order-kind" />
                    <span className="order-total">&nbsp;</span>
                    <span className="order-state">
                      <span className="state-pill is-pad">&nbsp;</span>
                      <small>&nbsp;</small>
                    </span>
                  </div>
                ))}
            </div>
          )}

          {pageCount > 1 && (
            <nav className="menu-pages" aria-label="Trang hoá đơn">
              {Array.from({ length: pageCount }, (_, i) => i + 1).map((n) => (
                <button
                  key={n}
                  type="button"
                  className="menu-page num"
                  aria-current={n === page ? 'page' : undefined}
                  onClick={() => setPageAt(n)}
                >
                  {n}
                </button>
              ))}
            </nav>
          )}
        </div>

        <aside className="orders-pane" aria-label="Chi tiết đơn">
          {picked && hasPane ? (
            <OrderDetailSheet
              key={picked.id}
              orderId={picked.id}
              variant="pane"
              onClose={() => setPickedId(null)}
              onVoided={async () => {
                setPickedId(null)
                await onVoided()
              }}
            />
          ) : (
            <div className="pane-empty">
              <div className="empty-icon" aria-hidden="true">
                👆
              </div>
              <p>Chạm vào một đơn để xem chi tiết, in lại hoặc huỷ.</p>
            </div>
          )}
        </aside>
      </div>

      {/* Màn hình hẹp: không có khoang bên phải thì chi tiết đơn mở bằng tấm trượt. */}
      {picked && !hasPane && (
        <OrderDetailSheet
          key={picked.id}
          orderId={picked.id}
          onClose={() => setPickedId(null)}
          onVoided={async () => {
            setPickedId(null)
            await onVoided()
          }}
        />
      )}
    </div>
  )
}
